import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useRequestTimebankDeposit } from "@/hooks/use-timebank";
import { SrIcon } from "@/components/ui/SrIcon";

interface RequestDepositDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  timeEntryId: string;
  assignmentId: string | null;
  taskDescription: string;
  /** Total varighet på timeføringen i timer */
  entryHours: number;
  /** Hvor mye av denne timeføringen som allerede er sendt til banken (pending+approved) */
  alreadyBanked: number;
}

export default function RequestDepositDialog({
  open,
  onOpenChange,
  userId,
  timeEntryId,
  assignmentId,
  taskDescription,
  entryHours,
  alreadyBanked,
}: RequestDepositDialogProps) {
  const requestMut = useRequestTimebankDeposit();
  // Rund til 2 desimaler så default-input ikke viser rå float (2,241388…).
  // Avrunder remaining ved kilden, så default, max og validering blir konsistente.
  const remaining = Math.round(Math.max(0, entryHours - alreadyBanked) * 100) / 100;
  const [hours, setHours] = useState("");
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (open) {
      setHours(remaining > 0 ? String(remaining) : "");
      setComment("");
    }
  }, [open, remaining]);

  const parsedHours = parseFloat(hours);
  const validHours =
    !isNaN(parsedHours) && parsedHours > 0 && parsedHours <= remaining + 0.0001;
  const canSubmit = validHours && !requestMut.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await requestMut.mutateAsync({
        user_id: userId,
        assignment_id: assignmentId,
        time_entry_id: timeEntryId,
        hours: parsedHours,
        comment: comment.trim() || undefined,
        task_description: taskDescription,
      });
      onOpenChange(false);
    } catch {
      // toast handled in hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">
            Send timer til timebank
          </DialogTitle>
          <DialogDescription>
            Daglig leder må godkjenne timene før de legges til timebanken.
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0: DialogContent er en grid (min-width:auto på items), så uten
            dette kan ikke boksen krympe og `truncate` på oppgaveteksten slår
            ikke inn — lang task_description sprengte boksen (feedback 25.06). */}
        <div className="min-w-0 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs space-y-0.5">
          <p className="font-medium text-foreground truncate">{taskDescription}</p>
          <p className="text-muted-foreground tabular-nums">
            Logget {entryHours.toFixed(1)} t
            {alreadyBanked > 0 && (
              <>
                {" · "}
                {alreadyBanked.toFixed(1)} t allerede sendt
              </>
            )}
            {" · "}
            {remaining.toFixed(1)} t igjen tilgjengelig
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Antall timer
            </label>
            <input
              type="number"
              step="0.25"
              min="0"
              max={remaining}
              className="mt-1 w-full h-9 rounded-md border-[1.5px] border-foreground bg-background px-2 text-sm tabular-nums"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              autoFocus
            />
            {parsedHours > remaining && (
              <p className="text-[11px] text-destructive mt-1">
                Kan ikke bank mer enn {remaining.toFixed(1)} t fra denne timeføringen.
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Kommentar (valgfritt)
            </label>
            <textarea
              rows={2}
              className="mt-1 w-full rounded-md border-[1.5px] border-foreground bg-background px-2 py-1.5 text-sm"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Begrunn hvorfor disse timene skal til timebank"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={requestMut.isPending}
          >
            Avbryt
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {requestMut.isPending ? (
              <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <SrIcon name="send" className="w-4 h-4 mr-1.5" />
            )}
            Send søknad
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
